'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { EXPLORE_FILTER_PARAM_KEYS } from '@/hotdesk-ui/helpers/explore-location-params';
export interface FilterValues {
  frequency?: string;
  priceGte?: number;
  priceLte?: number;
  typeIn?: string[] | string;
  amenitiesIdIn?: string[] | string;
  paymentTypeIn?: string[] | string;
  filterCapOrQtyGte?: number | string;
  quickBooking?: boolean;
  ratingGte?: string;
  userSubscriptionIdIn?: string[] | string;
}
export const isQuickBookingActive = (value: unknown): boolean => value === true || value === 'true';
interface FilterContextType {
  filterValues: FilterValues;
  setFilterValues: (value: ((prev: FilterValues) => FilterValues) | FilterValues) => void;
  processFilters: (value: Record<string, FilterValues[keyof FilterValues]>) => FilterValues;
  setPriceGte: (value: number) => void;
  setPriceLte: (value: number) => void;
  priceGte: number;
  priceLte: number;
}
const FilterContext = createContext<FilterContextType | undefined>(undefined);
export const FilterProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const searchParams = useSearchParams();
  const processFilters = useCallback((filters: Record<string, FilterValues[keyof FilterValues]>) => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (['typeIn', 'paymentTypeIn', 'amenitiesIdIn', 'userSubscriptionIdIn'].includes(key) && typeof value === 'string') {
        acc[key] = value.split(',');
      } else if (key === 'quickBooking') {
        if (isQuickBookingActive(value)) {
          acc[key] = true;
        }
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, FilterValues[keyof FilterValues]>);
  }, []);

  // Only explore filter keys — ignore lat/lon/active/lang so soft navigations
  // (e.g. navbar "Long-term offices") update plan checkboxes correctly.
  const filtersFromUrl = useMemo(() => {
    const newFilters: Record<string, string> = {};
    for (const key of EXPLORE_FILTER_PARAM_KEYS) {
      const value = searchParams.get(key);
      if (value) newFilters[key] = value;
    }
    return processFilters(newFilters);
  }, [searchParams, processFilters]);
  const [filterValues, setFilterValues] = useState<FilterValues>(filtersFromUrl);
  const [priceGte, setPriceGte] = useState<number>(0);
  const [priceLte, setPriceLte] = useState<number>(100000);

  // useSearchParams updates on router.push/replace, but not on replaceState
  // from the filters modal — so draft edits there are not wiped by this sync.
  useEffect(() => {
    setFilterValues(filtersFromUrl);
  }, [filtersFromUrl]);
  return <FilterContext.Provider value={{
    filterValues,
    setFilterValues,
    processFilters,
    priceGte,
    priceLte,
    setPriceGte,
    setPriceLte
  }} data-sentry-element="unknown" data-sentry-component="FilterProvider" data-sentry-source-file="FiltersContext.tsx">
            {children}
        </FilterContext.Provider>;
};
export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilterContext must be used within a filterProvider');
  }
  return context;
};